import { memo, useMemo } from "react";
import type { UIElement } from "../types";
import {
  buildAriaLabel,
  descendantLines,
  filterAndSort,
  inferRole,
} from "../lib/uiTree";

interface Props {
  elements: UIElement[];
  deviceWidth: number;
  deviceHeight: number;
}

function A11yOverlayImpl({ elements, deviceWidth, deviceHeight }: Props) {
  // filterAndSort + descendantLines both scan the elements + rebuild parent
  // maps. Memoize so auto-refresh re-renders don't re-scan unless the tree
  // actually changed.
  const ordered = useMemo(() => filterAndSort(elements), [elements]);

  return (
    <div
      className="pointer-events-none absolute inset-0"
      aria-hidden={false}
      id="a11y-layer"
    >
      {ordered.map((el) => {
        const aria = buildAriaLabel(el);
        const kids = descendantLines(elements, el.index);
        const text = kids.length ? `${aria}\n${kids.join("\n")}` : aria;
        const role = inferRole(el.type, el.clickable, el.editable);
        return (
          <div
            key={el.index}
            role={role}
            aria-label={aria}
            title={aria}
            data-element-index={String(el.index)}
            data-element-bounds={`${el.x},${el.y},${el.width},${el.height}`}
            data-element-type={el.type || undefined}
            data-element-text={el.text || undefined}
            data-element-content-desc={el.contentDesc || undefined}
            data-element-accessibility-id={el.accessibilityId || undefined}
            data-element-value={el.value || undefined}
            data-element-clickable={el.clickable ? "1" : undefined}
            data-element-editable={el.editable ? "1" : undefined}
            data-element-scrollable={el.scrollable ? "1" : undefined}
            data-element-focused={el.focused ? "1" : undefined}
            data-element-hittable={el.hittable === false ? "0" : undefined}
            className="a11y-el absolute box-border rounded-sm border border-transparent hover:outline hover:outline-1 hover:outline-cyan-400/60"
            style={{
              left: `${(100 * el.x) / deviceWidth}%`,
              top: `${(100 * el.y) / deviceHeight}%`,
              width: `${(100 * el.width) / deviceWidth}%`,
              height: `${(100 * el.height) / deviceHeight}%`,
              pointerEvents: "auto",
              color: "transparent",
              font: "0/0 a",
            }}
          >
            {text}
          </div>
        );
      })}
    </div>
  );
}

export const A11yOverlay = memo(A11yOverlayImpl);
